import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { ApiError } from '@/util/errors/api-error'
import { postQueryTableSources } from '../fetch/post-query-table-sources'
import { deleteTableSource } from '../fetch/delete-table-source'
import { TableSources, TableSourcePage, TableSourceQuery } from '../sources/schemas'

const noHiddenColumns: string[] = []

/** Owns source inventory requests and reconciles every attempted removal. */
export function useTableSources({
  tableId,
  query,
  onQueryChange,
  onRemoved,
  hiddenColumns = noHiddenColumns,
}: {
  tableId: string
  query: TableSourceQuery
  onQueryChange?: (query: TableSourceQuery) => void
  onRemoved?: () => void
  hiddenColumns?: string[]
}) {
  const progress = useSelector((state: RootState) => (state.tableAnalysis?.tableId === tableId ? state.tableAnalysis.run : null))
  // Poll responses replace the run object even when the source inventory has not changed.
  const sourceProgressKey = JSON.stringify([
    progress?.id,
    progress?.counts,
    progress?.sources.map(({ id, document_id, status, error }) => [id, document_id, status, error]),
  ])
  const [localOffset, setLocalOffset] = useState(0)
  const offset = onQueryChange ? query.offset : localOffset
  const searchFilenames = !hiddenColumns.includes('document_name')
  const requestQuery = useMemo(
    () => (query.search?.trim() ? { ...query, offset, search_filenames: searchFilenames } : offset === query.offset ? query : { ...query, offset }),
    [query, offset, searchFilenames]
  )
  const setOffset = useCallback((offset: number) => (onQueryChange ? onQueryChange({ ...query, offset }) : setLocalOffset(offset)), [onQueryChange, query])
  const [result, setResult] = useState<{ page: TableSourcePage; query: TableSourceQuery } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refresh, setRefresh] = useState(0)
  const reload = useCallback(() => setRefresh((value) => value + 1), [])
  const [removal, setRemoval] = useState<TableSources | null>(null)
  const [removing, setRemoving] = useState(false)
  const [removalError, setRemovalError] = useState<string | null>(null)
  const mutationInFlight = useRef(false)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    postQueryTableSources(tableId, requestQuery, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return
        if (requestQuery.offset > 0 && requestQuery.offset >= page.matched) {
          setOffset(Math.max(0, Math.ceil(page.matched / requestQuery.limit) - 1) * requestQuery.limit)
          return
        }
        setResult({ page, query: requestQuery })
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return
        if (reason instanceof ApiError && (reason.status === 403 || reason.status === 404)) setResult(null)
        setError(reason instanceof Error ? reason.message : 'Unable to load source files.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [tableId, requestQuery, sourceProgressKey, refresh, setOffset])

  const remove = async () => {
    if (!removal || mutationInFlight.current) return
    mutationInFlight.current = true
    setRemoving(true)
    setRemovalError(null)
    try {
      for (const source of removal) {
        await deleteTableSource(tableId, source.id)
        // A retry must only attempt files that have not already been removed.
        setRemoval((remaining) => remaining?.filter((item) => item.id !== source.id) ?? null)
      }
      setRemoval(null)
    } catch (reason) {
      setRemovalError(reason instanceof Error ? reason.message : 'Unable to remove this file.')
    } finally {
      mutationInFlight.current = false
      setRemoving(false)
      reload()
      onRemoved?.()
    }
  }
  return {
    page: result?.page ?? null,
    displayedQuery: result?.query ?? requestQuery,
    stale: result?.query !== requestQuery,
    loading,
    error,
    reload,
    offset,
    setOffset,
    removal,
    removing,
    removalError,
    remove,
    requestRemoval: (sources: TableSources) => {
      setRemovalError(null)
      setRemoval(sources)
    },
    cancelRemoval: () => setRemoval(null),
  }
}
